import React from "react";
import { MenuPane, MenuPaneHeader } from "../components/menu";
import { APIServices, TService, TServiceGroup } from "kpm-backend-interface";
import { fetchApi, useDataFecther, prefixHost } from "./utils";
import {
  AuthError,
  EmptyPlaceholder,
  ErrorMessage,
  LoadingPlaceholder,
} from "../components/common";
import { i18n } from "../i18n/i18n";
import "./services.scss";

export async function loaderServices({
  request,
}: any = {}): Promise<APIServices> {
  const res = await fetchApi("/api/services", {
    signal: request?.signal,
  });
  const json = await res.json();
  if (res.ok) {
    return json;
  } else {
    if (res.status === 401) {
      throw new AuthError(json.message);
    }
    throw new Error(json.message);
  }
}

export function Services() {
  const { res, loading, error } = useDataFecther<APIServices>(loaderServices);
  const { serviceGroups, bannerConfig } = res || {};

  const hasServiceGroups = Array.isArray(serviceGroups) && serviceGroups?.length > 0;

  // Helper to render multiple groups in the same column vertically
  const renderGroupsInColumn = (groups: TServiceGroup[]) => {
    if (!groups || groups.length === 0) return null;

    return (
      <div className="kpm-col">
        {groups.map((group) => (
          <div key={group.groupId} className="kpm-group-section">
            <h3 className="kpm-col-header">{group.groupName}</h3>
            <ul>
              {group.services.map((service) => (
                <li key={service.url}>
                  <a href={service.url}>{service.name}</a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    );
  };

  // Sorts the services in each group alphabetically by 'name'
  const sortGroupsServices = (groups: TServiceGroup[] | undefined): TServiceGroup[] | undefined => {
    if (!groups) return groups;
    return groups.map(group => ({
      ...group,
      services: [...group.services].sort((a, b) => a.name.localeCompare(b.name)),
    }));
  };

  const sortedServiceGroups = sortGroupsServices(serviceGroups);

  const renderColumn = (groups: TServiceGroup[] | undefined, column: number) => {
    if (!groups) return null;
    const columnGroups = groups.filter(group => group.column === column);
    return renderGroupsInColumn(columnGroups);
  };

  return (
    <MenuPane className="kpm-services" error={error}>
      <MenuPaneHeader title={i18n("KTH Services")}>
        <a
          title={i18n("Help / feedback for the personal menu")}
          href={prefixHost(
            "www",
            "/student/it/contact/contact-it-support-1.642185"
          )}
        >
          Help / feedback
        </a>
      </MenuPaneHeader>
      {bannerConfig?.show && (
        <div className="kpm-info-banner" role="status" aria-live="polite">
          <span>
            {i18n(bannerConfig.messageKey)}
            {' '}
            <a
              href={i18n("service_menu_change_link_url")}
              target="_blank"
              rel="noopener noreferrer"
            >
              {i18n("service_menu_change_link_text")}
            </a>
          </span>
        </div>
      )}
      <>
        <div className="kpm-col">
          <h3 className="kpm-col-header">{i18n("LADOK for Students")}</h3>
          <ul>
            <li>
              <h4>
                <a href="https://student.ladok.se/student/app/studentwebb/start">
                  {i18n("Home page")}
                </a>
              </h4>
              <p>
                {i18n(
                  "Relevant information right now. Course registration, Exam registration"
                )}
              </p>
            </li>
            <li>
              <h4>
                <a href="https://student.ladok.se/student/app/studentwebb/min-utbildning/alla">
                  {i18n("My education")}
                </a>
              </h4>
              <p>
                {i18n(
                  "Overview of your studies. Programmes, courses, results on courses"
                )}
              </p>
            </li>
            <li>
              <h4>
                <a href="https://student.ladok.se/student/app/studentwebb/examinationstillfallen/oppna-for-anmalan">
                  {i18n("Examinations")}
                </a>
              </h4>
              <p>{i18n("Sign up for examinations")}</p>
            </li>
            <li>
              <h4>
                <a href="https://student.ladok.se/student/app/studentwebb/intyg">
                  {i18n("Transcripts")}
                </a>
              </h4>
            </li>
            <li>
              <h4>
                <a href="https://student.ladok.se/student/app/studentwebb/examen-bevis">
                  {i18n("Degree Certificate")}
                </a>
              </h4>
              <p>{i18n("Apply for degree certificate")}</p>
            </li>
          </ul>
        </div>
        {loading && (
          <>
            <div className="kpm-col"><LoadingPlaceholder /></div>
            <div className="kpm-col"><LoadingPlaceholder /></div>
          </>
        )}
        {error && (
          <>
            <div className="kpm-col"><ErrorMessage error={error} /></div>
            <div className="kpm-col"></div>
          </>
        )}
        {renderColumn(sortedServiceGroups, 2)}
        {renderColumn(sortedServiceGroups, 3)}
      </>
    </MenuPane>
  );
}
